import { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { fetchCostData } from '../modules/api';
import useCostData from '../hooks/UseCostData';
import useUnitData from '../hooks/UseUnitData';
import { useConfig } from '../contexts/ConfigProvider';
import { EVENTS_NAME } from '../modules/events';

const MAX_RETRIES = 3;
const RETRY_DELAY = 3000;

const propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]).isRequired,
};

const defaultProps = {};

const CostData = ({ children }) => {
  const { unitInfo, setData, setIsLoading } = useCostData();
  const { data: { formConfig, facility } } = useUnitData();
  const {
    facilityId,
    unitId,
    moveInDate,
    insurance,
    lock,
    cardNumber,
  } = unitInfo;
  const facilityAlias = facility?.alias;

  const [retries, setRetries] = useState(0);
  const { triggerEvent } = useConfig();

  const hasFetchedOnceRef = useRef(false);
  const cardNumberWasEmptyRef = useRef(!cardNumber);

  useEffect(() => {
    const canFetch = facilityId
      && unitId
      && moveInDate
      && formConfig
      && facility;

    // If cardNumber was previously missing and is now added, allow re-fetch
    const cardNumberNowAvailable = cardNumber && cardNumberWasEmptyRef.current;

    if (!canFetch && !cardNumberNowAvailable) { return; }

    setIsLoading(true);

    fetchCostData(
      unitInfo,
      formConfig.insuranceKey,
      facility?.timezone,
      facility?.geo,
      facility?.invoicePeriod,
      facilityAlias,
    )
      .then((res) => res.json())
      .then(({ charges }) => {
        setIsLoading(false);

        if (charges.error) {
          if (retries < MAX_RETRIES) {
            setTimeout(() => {
              setRetries((prev) => prev + 1);
            }, RETRY_DELAY);
          } else {
            triggerEvent(EVENTS_NAME.ERROR, {
              action: 'retrieveCosts',
              errors: [charges.message],
            });
          }
        } else {
          setData(charges);
          hasFetchedOnceRef.current = true;

          // mark that cardNumber is now present
          if (cardNumber) {
            cardNumberWasEmptyRef.current = false;
          }
        }
      })
      .catch((e) => {
        if (retries < MAX_RETRIES) {
          setTimeout(() => {
            setRetries((prev) => prev + 1);
          }, RETRY_DELAY);
        } else {
          triggerEvent(EVENTS_NAME.ERROR, {
            action: 'retrieveCosts',
            errors: [e.message],
          });
          setIsLoading(false);
        }
      });
  }, [
    facilityId,
    unitId,
    moveInDate,
    insurance,
    lock,
    formConfig,
    facility,
    retries,
    cardNumber,
    unitInfo,
    triggerEvent,
    setIsLoading,
    setData,
  ]);

  return children;
};

CostData.propTypes = propTypes;
CostData.defaultProps = defaultProps;

export default CostData;
